import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient } from '../../api/api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface WhiteLabelConfig {
  institutionId: string;
  institutionName: string;
  logo: string;
  logoCompact: string;
  favicon: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily?: string;
  defaultThemeId?: string;
  loginBackground?: string;
  supportEmail?: string;
  supportPhone?: string;
  termsUrl?: string;
  privacyUrl?: string;
}

interface WhiteLabelContextValue {
  config: WhiteLabelConfig;
  loading: boolean;
  error: string | null;
  /** Re-fetch branding from the API */
  refresh: () => Promise<void>;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

const KOODOOKA_DEFAULTS: WhiteLabelConfig = {
  institutionId: '',
  institutionName: 'Koodooka',
  logo: '/brand/koodooka-outline-icon-red-512.png',
  logoCompact: '/brand/koodooka-outline-icon-red-512.png',
  favicon: '/favicon.ico',
  primaryColor: '#9E321F',
  secondaryColor: '#665B55',
  accentColor: '#B23E28',
  fontFamily: undefined,
  defaultThemeId: undefined,
  loginBackground: undefined,
  supportEmail: 'support@koodooka.com',
  supportPhone: undefined,
  termsUrl: undefined,
  privacyUrl: undefined,
};

// ─── CSS variable injection ──────────────────────────────────────────────────

function parseHexToRgb(hex: string): string | undefined {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return undefined;
  return [match[1], match[2], match[3]]
    .map((part) => parseInt(part, 16))
    .join(', ');
}

function applyCssVariables(config: WhiteLabelConfig): void {
  const root = document.documentElement;

  root.style.setProperty('--institution-primary', config.primaryColor);
  root.style.setProperty('--institution-secondary', config.secondaryColor);
  root.style.setProperty('--institution-accent', config.accentColor);
  const primaryRgb = parseHexToRgb(config.primaryColor);
  const secondaryRgb = parseHexToRgb(config.secondaryColor);
  const accentRgb = parseHexToRgb(config.accentColor);
  if (primaryRgb) root.style.setProperty('--institution-primary-rgb', primaryRgb);
  if (secondaryRgb) root.style.setProperty('--institution-secondary-rgb', secondaryRgb);
  if (accentRgb) root.style.setProperty('--institution-accent-rgb', accentRgb);
  root.style.setProperty('--institution-logo', `url(${config.logo})`);
  root.style.setProperty('--institution-logo-compact', `url(${config.logoCompact})`);

  if (config.fontFamily) {
    root.style.setProperty('--institution-font-family', config.fontFamily);
  }

  if (config.loginBackground) {
    root.style.setProperty('--institution-login-bg', `url(${config.loginBackground})`);
  }
}

function setFavicon(href: string): void {
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const WhiteLabelContext = createContext<WhiteLabelContextValue | undefined>(undefined);

export function useWhiteLabel(): WhiteLabelContextValue {
  const ctx = useContext(WhiteLabelContext);
  if (!ctx) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider');
  }
  return ctx;
}

// ─── Provider ────────────────────────────────────────────────────────────────

interface WhiteLabelProviderProps {
  /** Institution ID to fetch branding for. If omitted, Koodooka defaults apply. */
  institutionId?: string;
  children: ReactNode;
}

export const WhiteLabelProvider: React.FC<WhiteLabelProviderProps> = ({
  institutionId,
  children,
}) => {
  const [config, setConfig] = useState<WhiteLabelConfig>(KOODOOKA_DEFAULTS);
  const [loading, setLoading] = useState<boolean>(!!institutionId);
  const [error, setError] = useState<string | null>(null);

  const fetchBranding = useCallback(async () => {
    if (!institutionId) {
      setConfig(KOODOOKA_DEFAULTS);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const branding = await apiClient.get<WhiteLabelConfig>(
        `/institutions/${institutionId}/branding`,
      );
      setConfig(branding);
    } catch {
      // Fall back to Koodooka defaults on failure
      setConfig(KOODOOKA_DEFAULTS);
      setError('Failed to load institution branding. Using defaults.');
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  // Fetch branding on mount or when institutionId changes
  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  // Apply side-effects whenever config changes
  useEffect(() => {
    applyCssVariables(config);
    setFavicon(config.favicon);
    document.title = config.institutionName
      ? `${config.institutionName} Admin`
      : 'Koodooka Admin';
  }, [config]);

  return (
    <WhiteLabelContext.Provider value={{ config, loading, error, refresh: fetchBranding }}>
      {children}
    </WhiteLabelContext.Provider>
  );
};
